import { Button, Select } from "antd";
import React from "react";
import { REGION_OPTIONS } from "../../OpenSpace/regions";
import styles from "./ImportFloorplanModal.module.css";

export interface Organization {
  name: string;
  id: string;
}

export interface Site {
  name: string;
  id: string;
}

export interface SiteSheet {
  name: string;
  id: string;
  imageId?: string;
  sheetStats?: {
    videoCaptures?: {
      count?: number;
    };
  };
}

type OpenSpaceSheetImportPanelProps = {
  selectedRegion?: string;
  selectedOrganization?: string;
  selectedSite?: string;
  selectedSiteSheet?: string;
  organizations: Organization[];
  sites: Site[];
  siteSheets: SiteSheet[];
  orgLoading: boolean;
  siteLoading: boolean;
  sheetLoading: boolean;
  openSpaceLoading: boolean;
  openSpaceError?: string | null;
  onRegionChange: (value: string) => void;
  onOrganizationChange: (value: string) => void;
  onSiteChange: (value: string) => void;
  onSheetChange: (value: string) => void;
  onUseSheet: () => void;
};

export const OpenSpaceSheetImportPanel = ({
  selectedRegion,
  selectedOrganization,
  selectedSite,
  selectedSiteSheet,
  organizations,
  sites,
  siteSheets,
  orgLoading,
  siteLoading,
  sheetLoading,
  openSpaceLoading,
  openSpaceError,
  onRegionChange,
  onOrganizationChange,
  onSiteChange,
  onSheetChange,
  onUseSheet,
}: OpenSpaceSheetImportPanelProps) => (
  <div className={styles["import-column"]}>
    <div className={styles["import-column-title"]}>OpenSpace Sheet</div>
    <div className={styles["import-column-body"]}>
      <div className={styles["import-field"]}>
        <div className={styles["import-field-label"]}>Region</div>
        <Select
          showSearch
          optionFilterProp="label"
          className={styles["import-field-control"]}
          placeholder="Select a Region"
          onChange={onRegionChange}
          value={selectedRegion}
          options={REGION_OPTIONS}
        />
      </div>
      <div className={styles["import-field"]}>
        <div className={styles["import-field-label"]}>Organization</div>
        <Select
          showSearch
          className={styles["import-field-control"]}
          placeholder="Select an Organization"
          onChange={onOrganizationChange}
          value={selectedOrganization}
          loading={orgLoading}
          disabled={!selectedRegion}
          filterOption={(input, option) => {
            const label = typeof option?.label === "string" ? option.label : "";
            return label.toLowerCase().indexOf(input.toLowerCase().trim()) >= 0;
          }}
          options={organizations.map((org) => ({
            label: org.name,
            value: org.id,
          }))}
        />
      </div>
      <div className={styles["import-field"]}>
        <div className={styles["import-field-label"]}>Site</div>
        <Select
          showSearch
          className={styles["import-field-control"]}
          placeholder="Select a Site"
          onChange={onSiteChange}
          value={selectedSite}
          loading={siteLoading}
          disabled={!selectedOrganization}
          filterOption={(input, option) => {
            const label = typeof option?.label === "string" ? option.label : "";
            return label.toLowerCase().indexOf(input.toLowerCase().trim()) >= 0;
          }}
          options={sites.map((site) => ({
            label: site.name,
            value: site.id,
          }))}
        />
      </div>
      <div className={styles["import-field"]}>
        <div className={styles["import-field-label"]}>Sheet</div>
        <Select
          showSearch
          className={styles["import-field-control"]}
          placeholder="Select a Sheet"
          onChange={onSheetChange}
          value={selectedSiteSheet}
          loading={sheetLoading}
          disabled={!selectedSite}
          filterOption={(input, option) => {
            const labelText =
              option?.label?.props?.children || option?.label || "";
            return (
              labelText.toLowerCase().indexOf(input.toLowerCase().trim()) >= 0
            );
          }}
          options={siteSheets.map((sheet) => ({
            label: (
              <span
                style={{
                  color:
                    sheet.sheetStats?.videoCaptures?.count &&
                    sheet.sheetStats.videoCaptures.count > 0
                      ? "#008000"
                      : "#ca3433",
                }}
              >
                {sheet.name}
              </span>
            ),
            value: sheet.id,
          }))}
        />
      </div>
      {openSpaceError && (
        <div className={styles["import-error"]}>{openSpaceError}</div>
      )}
      <div className={styles["import-actions"]}>
        <Button
          type="primary"
          onClick={onUseSheet}
          loading={openSpaceLoading}
          disabled={!selectedSiteSheet || openSpaceLoading}
        >
          Use OpenSpace Sheet
        </Button>
      </div>
    </div>
  </div>
);
